/**
 * Canonical status vocabularies.
 *
 * These constants are the single source of truth for every status value in
 * the platform. Task 02 seeds its reference tables from exactly these lists,
 * and every module compares against these constants rather than string
 * literals. A status that is not here does not exist.
 */
export const RESERVATION_STATUS = {
    DRAFT: 'DRAFT',
    INQUIRY: 'INQUIRY',
    PENDING: 'PENDING',
    PENCIL: 'PENCIL',
    CONFIRMED: 'CONFIRMED',
    MODIFIED: 'MODIFIED',
    PRECHECKED_IN: 'PRECHECKED_IN',
    PARTIALLY_PRECHECKED_IN: 'PARTIALLY_PRECHECKED_IN',
    CHECKED_IN: 'CHECKED_IN',
    CHECKED_OUT: 'CHECKED_OUT',
    CANCELLED: 'CANCELLED',
    NO_SHOW: 'NO_SHOW',
    EXPIRED: 'EXPIRED',
    MAINTENANCE: 'MAINTENANCE',
};
/**
 * Reservation statuses seeded by Task 02. `CHECKED_IN` and `CHECKED_OUT` are
 * defined in the type above because the Reservation Engine's state machine
 * reserves them, but they are not seeded this phase — they arrive with PMS
 * check-in/check-out support.
 */
export const SEEDED_RESERVATION_STATUSES = [
    RESERVATION_STATUS.DRAFT,
    RESERVATION_STATUS.PENDING,
    RESERVATION_STATUS.CONFIRMED,
    RESERVATION_STATUS.MODIFIED,
    RESERVATION_STATUS.CANCELLED,
    RESERVATION_STATUS.EXPIRED,
];
/**
 * Payment statuses.
 *
 * SPEC CORRECTION (SPEC_CORRECTIONS.md #4): Task 02's seed list omitted
 * PARTIALLY_CAPTURED, PARTIALLY_REFUNDED and EXPIRED, and spelled VOIDED as
 * "Void" — none of which can represent the state machine Task 19 defines.
 * This list is the corrected superset and is what Task 02 must seed.
 */
export const PAYMENT_STATUS = {
    PENDING: 'PENDING',
    AUTHORIZED: 'AUTHORIZED',
    PARTIALLY_CAPTURED: 'PARTIALLY_CAPTURED',
    CAPTURED: 'CAPTURED',
    PARTIALLY_REFUNDED: 'PARTIALLY_REFUNDED',
    REFUNDED: 'REFUNDED',
    VOIDED: 'VOIDED',
    FAILED: 'FAILED',
    EXPIRED: 'EXPIRED',
};
/** Payment states from which no further transition is permitted. */
export const TERMINAL_PAYMENT_STATUSES = [
    PAYMENT_STATUS.REFUNDED,
    PAYMENT_STATUS.VOIDED,
    PAYMENT_STATUS.FAILED,
    PAYMENT_STATUS.EXPIRED,
];
export const ROOM_STATUS = {
    AVAILABLE: 'AVAILABLE',
    OCCUPIED: 'OCCUPIED',
    OUT_OF_SERVICE: 'OUT_OF_SERVICE',
    MAINTENANCE: 'MAINTENANCE',
    CLEANING: 'CLEANING',
    BLOCKED: 'BLOCKED',
    INACTIVE: 'INACTIVE',
};
export const PROPERTY_STATUS = {
    DRAFT: 'DRAFT',
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
    MAINTENANCE: 'MAINTENANCE',
    ARCHIVED: 'ARCHIVED',
};
export const PROPERTY_GROUP_STATUS = {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
};
export const GUEST_STATUS = {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
    BLOCKED: 'BLOCKED',
};
/** Where a reservation originated. */
export const RESERVATION_SOURCE = {
    DIRECT: 'DIRECT',
    OTA: 'OTA',
    PMS: 'PMS',
    PHONE: 'PHONE',
    ADMIN: 'ADMIN',
};
/**
 * Tenant lifecycle.
 *
 * PENDING exists so provisioning is not atomic-or-nothing: a tenant can be
 * created, have its domains and first administrator set up, and only then be
 * made ACTIVE. Serving traffic to a half-provisioned tenant is worse than
 * refusing it.
 */
export const TENANT_STATUS = {
    PENDING: 'PENDING',
    ACTIVE: 'ACTIVE',
    SUSPENDED: 'SUSPENDED',
    ARCHIVED: 'ARCHIVED',
};
export const PLATFORM_TENANT_ID = '00000000-0000-0000-0000-000000000001';
/**
 * Whether a tenant may serve requests.
 *
 * Only ACTIVE. Written as an allowlist rather than as `!== SUSPENDED` so that
 * a status added later fails closed instead of silently becoming serviceable.
 */
export function isTenantOperational(status) {
    return status === TENANT_STATUS.ACTIVE;
}
/**
 * The statuses a cancellation or payment policy moves between.
 *
 * `DRAFT` is the only editable state. Once a policy becomes `ACTIVE` it is
 * immutable and can only be copied to a new `DRAFT`. `INACTIVE` is retired
 * from new rate plans but remains attached to existing ones. `ARCHIVED` is
 * intentionally absent: a policy is either in force, being drafted, or
 * inactive — there is no terminal archived state.
 */
export const POLICY_STATUS = {
    DRAFT: 'DRAFT',
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
};
/**
 * The statuses a rate plan moves between.
 *
 * Rate plans are distinct from payment/cancellation policies: they can be
 * archived because a rate plan can be retired without breaking historical
 * policy references.
 */
export const RATE_PLAN_STATUS = {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
    ARCHIVED: 'ARCHIVED',
};
export const ROOM_TYPE_STATUS = {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
    ARCHIVED: 'ARCHIVED',
};
//# sourceMappingURL=statuses.js.map